// The full write-up behind each reason (p1–p21) and strategy (s1–s27):
// a plain explanation in short paragraphs, and the sources it rests on.
// Drafted 12 Oct 2026 from the reference doc; p1, p21 and s2 approved by Craig as the voice.
export const WRITEUPS = {
  p1: {
    paras: [
      "When you go to start something hard, your brain registers a small jolt of discomfort. It might be boredom, worry or self-doubt.",
      "Putting the task off makes that feeling go away straight away, so your brain learns that avoiding it works. It isn't laziness, and a better schedule won't fix it.",
      "What helps is making the start feel smaller or kinder, so the discomfort never gets the chance to build.",
    ],
    research: [
      "Tice, D.M. & Baumeister, R.F. (1997). Longitudinal study of procrastination, performance, stress, and health. Psychological Science.",
      "Sirois, F. & Pychyl, T. (2013). Procrastination and the priority of short-term mood regulation. Social and Personality Psychology Compass.",
    ],
  },
  p2: {
    paras: [
      "Putting a task in your calendar can feel like deciding to do it. Often it isn't. The plan looks finished, but underneath it is a wish.",
      "Truly choosing something means sitting with some discomfort, and scheduling lets you skip that part. Then the time arrives, something more appealing turns up, and nothing pulls you back to the task.",
      "So the question isn't only when you'll do it. It's whether you've actually decided to.",
    ],
    research: [
      "Steel, P. (2007). The nature of procrastination. Psychological Bulletin.",
    ],
  },
  p3: {
    paras: [
      "Sometimes your brain swaps one task for another without asking you. You meant to work on one thing, drifted to another, and never actually decided to.",
      "The swap was often a reasonable one. The guilt comes because you didn't make it on purpose.",
      "Making the trade-off a conscious choice takes most of that guilt away. Name what you're doing instead, why you're doing it, and when you'll go back.",
    ],
    research: [],
  },
  p4: {
    paras: [
      "People follow through on things they've chosen and push back on things that feel imposed. That holds even when you're the one who imposed the task.",
      "A task that's on your list because \"I should\" gets more resistance than one that's there because \"I chose to\", even when the task is identical. This isn't stubbornness. It's a deep need to feel in charge of your own life.",
      "That's why it helps to find your own reason for doing it. Then the task becomes your choice rather than an order.",
    ],
    research: [
      "Deci, E.L. & Ryan, R.M. (1985). Intrinsic motivation and self-determination in human behavior. Plenum.",
      "Ryan, R.M. & Deci, E.L. (2000). Self-determination theory and the facilitation of intrinsic motivation, social development, and well-being. American Psychologist.",
    ],
  },
  p5: {
    paras: [
      "Sometimes you put a task off not because you dislike it, but to protect how you see yourself. A task you haven't started can't be judged as a failure.",
      "This hits hardest on the work you care about most. The more it matters, the higher the bar feels, until starting seems impossible.",
      "Underneath it is a fear of being judged: first the work, then you. Giving yourself permission to do a rough first version brings the bar back down to somewhere you can reach.",
    ],
    research: [
      "Frost, R.O., Marten, P., Lahart, C., & Rosenblate, R. (1990). The dimensions of perfectionism. Cognitive Therapy and Research.",
      "Dweck, C.S. (2006). Mindset: The New Psychology of Success. Random House.",
    ],
  },
  p6: {
    paras: [
      "There are five steps between knowing something needs doing and truly committing to it. You notice it, intend to do it, schedule it, decide to do it, and then commit to it.",
      "Most planning stops at the third step and treats it as the fifth. That gap is where most procrastination lives.",
      "Scheduling is a practical step. Committing is an emotional one. They feel alike when you make the plan, and they lead to very different results when it's time to start.",
    ],
    research: [],
  },
  p7: {
    paras: [
      "Making a plan gives you a small reward. Your brain registers \"I've dealt with this\", even though nothing has been done yet.",
      "That feeling takes some of the pressure off. The trouble is that the pressure was what would have got you started.",
      "A detailed plan can quietly become a replacement for the work instead of the start of it. If planning felt satisfying and nothing has moved, that's what happened.",
    ],
    research: [
      "Masicampo, E.J. & Baumeister, R.F. (2011). Consider it done! Plan making can eliminate the cognitive effects of unfulfilled goals. Journal of Personality and Social Psychology.",
    ],
  },
  p8: {
    paras: [
      "Once you've really decided to do something, deciding exactly when, where and how you'll do it makes you far more likely to follow through.",
      "The plan works best as an \"if-then\": \"When I've finished lunch at my desk, I'll open the report.\" The decision is already made, so there's nothing to argue about when the moment comes.",
      "A vague plan like \"sometime this week\" leaves the decision for the moment itself. That's exactly when it's hardest to make.",
    ],
    research: [
      "Gollwitzer, P.M. (1999). Implementation intentions: Strong effects of simple plans. American Psychologist.",
      "Gollwitzer, P.M. & Sheeran, P. (2006). Implementation intentions and goal achievement: A meta-analysis of effects and processes. Advances in Experimental Social Psychology.",
    ],
  },
  p9: {
    paras: [
      "Something will try to pull you away from the task. It's rarely distraction in general. It's your phone, an email you could answer, or a show you're halfway through.",
      "Naming that specific pull ahead of time makes it much easier to resist. Once you've named it, giving in becomes a choice you can see rather than a habit that takes over.",
      "It helps even more to decide your response in advance: \"If I reach for my phone, I'll put it in the other room.\" You make that call while your head is clear, not in the moment when you're tired.",
    ],
    research: [
      "Gollwitzer, P.M. (1999). Implementation intentions: Strong effects of simple plans. American Psychologist.",
    ],
  },
  p10: {
    paras: [
      "Most procrastination doesn't happen when you plan. It happens in the 30 seconds when it's time to start and your brain begins to negotiate.",
      "In that moment, the discomfort of starting is right in front of you, and so is the relief of not starting. The cost of waiting feels far away. A good plan doesn't protect you from this.",
      "That's why the moment of starting deserves as much attention as the plan. It's where the real battle is.",
    ],
    research: [
      "Steel, P. (2007). The nature of procrastination. Psychological Bulletin.",
    ],
  },
  p11: {
    paras: [
      "Important tasks that aren't urgent often never make it onto your list at all. When you plan, your brain steers you toward easy or urgent things and quietly leaves them out.",
      "They don't go away. They sit in the background as guilt and low-level worry, making everything else feel heavier without you ever looking at them directly.",
      "Because you never face that task, you never deal with it. The most damaging procrastination is the kind you can't see.",
    ],
    research: [],
  },
  p12: {
    paras: [
      "When a task has too many possible approaches or too many unknowns, your brain stalls rather than pick one that might be wrong.",
      "This feels different from ordinary procrastination. You want to start, but you can't get going. The feeling underneath is closer to a fear of choosing wrong than to dread or boredom.",
      "The problem is the number of decisions in front of you, not any one task. Taking a task off the pile won't fix it.",
    ],
    research: [
      "Iyengar, S.S. & Lepper, M.R. (2000). When choice is demotivating: Can one desire too much of a good thing? Journal of Personality and Social Psychology.",
    ],
  },
  p13: {
    paras: [
      "When you look at everything you have to do and see a pile of unpleasant things, your brain does a quick sum. It decides that nothing you do will make a real dent.",
      "So it shuts down, not on one task but on all of them. This is different from putting off one thing, and taking one task off the pile won't fix it.",
      "The problem is the combined weight, not any single item. Once you recognize that, you can narrow your view to one thing, and the weight becomes something you can act on.",
    ],
    research: [
      "Steel, P. (2007). The nature of procrastination. Psychological Bulletin.",
    ],
  },
  p14: {
    paras: [
      "One missed task is chance. Four missed tasks of the same kind, at the same time of day or with the same feeling attached, are a pattern.",
      "Most people don't spot their own patterns unless something shows them. Knowing \"I always push admin tasks past 4pm\" is worth more than any single reminder.",
      "Look at a pattern with curiosity, not judgment. Then you know what to change.",
    ],
    research: [
      "Sirois, F.M. & Pychyl, T.A. (Eds.) (2016). Procrastination, Health, and Well-Being. Academic Press.",
    ],
  },
  p15: {
    paras: [
      "Your brain thinks about your future self a bit like a stranger. That makes it easy to hand them the hard work, and \"I'll deal with it later\" feels perfectly reasonable.",
      "Bringing that future self closer helps. \"How will you feel tonight if this isn't done?\" works better than \"this is important\", because it puts the consequence somewhere you can feel it.",
      "The more real your future self feels, the harder it is to leave things for them.",
    ],
    research: [
      "Hershfield, H.E. (2011). Future self-continuity: How conceptions of the future self transform intertemporal choice. Annals of the New York Academy of Sciences.",
      "Hershfield, H.E., et al. (2011). Increasing saving behavior through age-progressed renderings of the future self. Journal of Marketing Research.",
    ],
  },
  p16: {
    paras: [
      "The further away a consequence is, the less it moves you. A task also feels less motivating when you're unsure it'll pay off, or when something more tempting is close at hand.",
      "That's how important tasks quietly turn into crises. When the urgency finally arrives, it usually brings stress and fewer options with it.",
      "The real damage happens in the quiet stretch before that, when nothing forces you to act and putting it off seems to cost nothing.",
    ],
    research: [
      "Steel, P. & König, C.J. (2006). Integrating theories of motivation. Academy of Management Review.",
      "Steel, P. (2007). The nature of procrastination. Psychological Bulletin.",
    ],
  },
  p17: {
    paras: [
      "Beating yourself up after putting something off makes you more likely to put it off again, not less.",
      "Guilt is uncomfortable, and your brain avoids discomfort. The quickest way to avoid the guilt is to keep avoiding the task. That's how the cycle feeds itself.",
      "The fastest way back is to admit what happened without judging yourself, then pick one small next step. Shame shuts doors. Kindness to yourself opens them.",
    ],
    research: [
      "Neff, K.D. (2003). Self-compassion: An alternative conceptualization of a healthy attitude toward oneself. Self and Identity.",
      "Neff, K.D. & Germer, C. (2013). A pilot study and randomized controlled trial of the mindful self-compassion program. Journal of Clinical Psychology.",
    ],
  },
  p18: {
    paras: [
      "Sometimes you aren't resisting the task. You're resisting the version of it you've pictured in your head, and that picture may be out of date.",
      "Often the task can be done differently. You might take a different approach, work somewhere else, shrink the scope or get help.",
      "When you see an easier way to do it, the dread of the hard version shrinks. The resistance often disappears with it.",
    ],
    research: [],
  },
  p19: {
    paras: [
      "Habits form when you do the same thing at the same time and in the same place, over and over. Eventually the setting itself starts you off without you having to think about it.",
      "When your routine is disrupted, that cue is missing. Starting then takes deliberate effort, which is tiring and fails more often.",
      "It's worth knowing this when you're travelling or out of your usual rhythm. Habits also form faster when something enjoyable is attached to them.",
    ],
    research: [
      "Wood, W. & Neal, D.T. (2007). A new look at habits and the habit-goal interface. Psychological Review.",
      "Lally, P., et al. (2010). How are habits formed: Modelling habit formation in the real world. European Journal of Social Psychology.",
    ],
  },
  p20: {
    paras: [
      "Self-control seems to run down over the day. Every decision, every push of willpower and every draining conversation takes something out of it.",
      "As it runs low, starting hard things and resisting temptation get harder. That's part of why procrastination often shows up in the afternoon and evening, and why a tough meeting can wreck the rest of your day.",
      "Researchers still debate exactly how this works. The practical point holds: your capacity for self-control changes across the day, and what you've already asked of it matters.",
    ],
    research: [
      "Baumeister, R.F., Bratslavsky, E., Muraven, M., & Tice, D.M. (1998). Ego depletion: Is the active self a limited resource? Journal of Personality and Social Psychology.",
    ],
  },
  p21: {
    paras: [
      "Some tasks have real consequences for your health, money, work or relationships. To make that pressure easier to live with, your brain quietly relabels them from \"I need to\" to \"I should\".",
      "\"Should\" sounds optional. The cost starts to feel far away, and something far away is easy to put off.",
      "You don't do this on purpose. It happens below the surface, which is why it helps to say the real word out loud: is this a should, or a need?",
    ],
    research: [
      "Steel, P. (2007). The nature of procrastination. Psychological Bulletin.",
      "Hershfield, H.E. (2011). Future self-continuity: How conceptions of the future self transform intertemporal choice. Annals of the New York Academy of Sciences.",
      "Trivers, R. (2011). The Folly of Fools: The Logic of Deceit and Self-Deception in Human Life. Basic Books.",
    ],
  },
  s1: {
    paras: [
      "Most people think you have to feel motivated before you start. It actually works the other way round.",
      "Doing something, even something small and imperfect, creates the feeling that makes the next step possible. Motivation comes out of action.",
      "Waiting to feel ready is a trap. Start, and let the motivation catch up with you.",
    ],
    research: [
      "Lewinsohn, P.M. (1974). A behavioral approach to depression. In R.J. Friedman & M.M. Katz (Eds.), Psychology of depression. Winston.",
      "Jacobson, N.S., Martell, C.R., & Dimidjian, S. (2001). Behavioral activation treatment for depression: Returning to contextual roots. Clinical Psychology: Science and Practice.",
      "Dimidjian, S., et al. (2011). The origins and current status of behavioral activation treatments for depression. Annual Review of Clinical Psychology.",
    ],
  },
  s2: {
    paras: [
      "Starting is rarely hard because of the task itself. It's hard because your brain pictures the whole thing at once.",
      "So pick a first step so small your brain can't argue with it. Open the document. Put on your running shoes. Write one sentence.",
      "Once you've started, an unfinished task nags at you. That pull makes it easier to keep going than to stop.",
    ],
    research: [
      "Allen, D. (2001). Getting Things Done: The Art of Stress-Free Productivity. Viking.",
      "Gollwitzer, P.M. (1999). Implementation intentions: Strong effects of simple plans. American Psychologist.",
      "Zeigarnik, B. (1927). Das Behalten erledigter und unerledigter Handlungen. Psychologische Forschung.",
    ],
  },
  s3: {
    paras: [
      "Most rewards come when a task is finished. For a big or dreaded task, the finish line is so far off that the reward does nothing for you at the moment you need it most, which is the moment you start.",
      "So reward yourself for starting, however far you get. The reward doesn't need to be big. It needs to be real, immediate and tied to beginning.",
      "Over time, starting itself begins to feel good. That makes the next start less of a fight.",
    ],
    research: [
      "Skinner, B.F. (1938). The Behavior of Organisms: An Experimental Analysis. Appleton-Century-Crofts.",
      "Premack, D. (1959). Toward empirical behavior laws: I. Positive reinforcement. Psychological Review.",
    ],
  },
  s4: {
    paras: [
      "Most of us look at our list and see what's still undone. That leaves you feeling behind, however much you've done.",
      "Research on people at work found that making progress on meaningful work, even a little, did more for their motivation on a given day than anything else.",
      "The catch is that you have to notice it. Marking small wins on purpose, like a task started, a paragraph written or a call made, gives you a lift that feeds your next action.",
    ],
    research: [
      "Amabile, T. & Kramer, S. (2011). The Progress Principle: Using Small Wins to Ignite Joy, Engagement, and Creativity at Work. Harvard Business Review Press.",
      "Amabile, T.M. & Kramer, S.J. (2011). The power of small wins. Harvard Business Review.",
    ],
  },
  s5: {
    paras: [
      "Commit to working on the task for 10 minutes. When the time is up, you're genuinely allowed to stop without feeling guilty.",
      "Ten minutes is too small for your brain to resist. Knowing you can stop removes the feeling that this will take forever, which is often the very thing stopping you from starting.",
      "Most people keep going past 10 minutes, because starting builds its own momentum. If you do stop, you kept the deal, and that counts as a win.",
    ],
    research: [],
  },
  s6: {
    paras: [
      "When you can't face the hard task, fighting yourself head-on often makes it worse. Instead, use something easier as a deliberate bridge.",
      "That can be something you enjoy, done briefly to lift your mood until starting feels possible. Or it can be a useful but lower-stakes task, so the energy you'd spend avoiding turns into real progress somewhere else.",
      "The limit is what makes it work. Decide in advance what the easier thing is, when it ends and when you'll switch to the hard task. Without that, the warm-up turns into avoidance.",
    ],
    research: [
      "Perry, J. (2012). The Art of Procrastination. Workman Publishing.",
      "Fredrickson, B.L. (2001). The role of positive emotions in positive psychology. American Psychologist.",
    ],
  },
  s7: {
    paras: [
      "Making a firm decision ahead of time takes the choice out of the moment when you're most likely to give in.",
      "Decide when, where and how you'll do it. Name what's likely to pull you away and what you'll do when it does. Telling someone else adds weight, because backing out then costs more.",
      "Your future self will face temptations you can predict now. You make the call while your head is clear, so there's nothing left to decide when the moment comes.",
    ],
    research: [
      "Gollwitzer, P.M. (1999). Implementation intentions: Strong effects of simple plans. American Psychologist.",
    ],
  },
  s8: {
    paras: [
      "Freezing often comes from holding too many options in your head at once. The answer is fewer decisions, not better ones.",
      "Narrow it down to three options at most. Use a simple tiebreaker, or rule options out until one is left.",
      "An imperfect choice made now is almost always better than a perfect one made late.",
    ],
    research: [],
  },
  s9: {
    paras: [
      "When everything feels heavy, it's usually because you're looking at all of it at once. Holding the whole list in your head while you work on one item wears you down.",
      "So pick one task and give it a set period of time. Everything else is paused for that time. It isn't cancelled.",
      "The rest of the pile is still there afterwards. While you work, though, it stops competing for your attention, and the task in front of you feels lighter.",
    ],
    research: [],
  },
  s10: {
    paras: [
      "When you catch yourself saying you \"should\" do something, ask yourself honestly whether it's a should or a need. A should is something you'd like to do but could live without. A need has real consequences if it doesn't happen.",
      "If it's a need, ask a second question: what actually happens if this doesn't get done? Picturing the answer clearly brings the consequence out of the distant future and into today.",
      "This isn't about scaring yourself or inventing urgency. Some tasks really are shoulds, and they're fine to put off. The question only matters when you've been hiding a real need from yourself.",
    ],
    research: [],
  },
  s11: {
    paras: [
      "Criticizing yourself after you've put something off makes you feel worse. Your brain avoids that feeling by putting things off again.",
      "Kindness breaks the cycle. Acknowledge what happened without judging yourself, and recognize that avoiding things is a normal human response. Then pick one small next step.",
      "People who treat their setbacks this way recover faster and try again sooner. Their habits also last longer.",
    ],
    research: [
      "Neff, K.D. (2003). Self-compassion: An alternative conceptualization of a healthy attitude toward oneself. Self and Identity.",
      "Neff, K.D. (2003). Development and validation of a scale to measure self-compassion. Self and Identity.",
    ],
  },
  s12: {
    paras: [
      "Deciding not to start feels like doing nothing. It's still a decision, and it has consequences of its own.",
      "While the task waits, stress builds and the deadline moves closer. Carrying an unfinished task around also drains your mental energy, even when you aren't working on it.",
      "When you put something off, you aren't avoiding a cost. You're choosing a different one, and it helps to see which one you're choosing.",
    ],
    research: [],
  },
  s13: {
    paras: [
      "When a task feels unpleasant, the natural move is to sweep any thought of what happens later under the rug. A cost that feels far away barely counts. That's why putting it off so often wins.",
      "Before you decide not to act, stop and picture yourself in the future. Ask yourself what it will feel like if the worst comes to worst because you didn't act now.",
      "Be specific and honest. You aren't trying to scare yourself. You're bringing a future cost close enough to count while you're still making the choice.",
    ],
    research: [
      "Hershfield, H.E. (2011). Future self-continuity: How conceptions of the future self transform intertemporal choice. Annals of the New York Academy of Sciences.",
      "Steel, P. & König, C.J. (2006). Integrating theories of motivation. Academy of Management Review.",
    ],
  },
  s14: {
    paras: [
      "Your capacity for self-control changes across the day. So match each task to the energy you have when you do it.",
      "Put the demanding tasks in the hours when you're sharpest. Save low-stakes tasks for when you're drained.",
      "Feeling depleted is a real condition, not a character flaw. Seeing it that way turns vague self-criticism into something useful: a reason to schedule differently.",
    ],
    research: [],
  },
  s15: {
    paras: [
      "Once a habit of avoiding something sets in, it keeps itself going. The same time, place or method sets off the same resistance, almost automatically.",
      "Change one thing on purpose. Try a different time, a different place, a different approach, or working alongside someone else. The novelty can interrupt the avoidance before it starts.",
      "The change needs to be real enough to break the routine, and small enough that you'll actually do it.",
    ],
    research: [],
  },
  s16: {
    paras: [
      "Willpower runs out. Doing something because it connects to what you care about lasts longer and keeps renewing itself.",
      "When you lose sight of why a task matters, it turns into pure obligation. Obligation has nothing driving it.",
      "Make the connection concrete: \"This connects to X, which matters to me because Y.\" That brings the value of the outcome close enough to feel now.",
    ],
    research: [
      "Ryan, R.M. & Deci, E.L. (2000). Self-determination theory and the facilitation of intrinsic motivation, social development, and well-being. American Psychologist.",
      "Hayes, S.C., et al. (2006). Acceptance and commitment therapy. Behaviour Research and Therapy.",
    ],
  },
  s17: {
    paras: [
      "The future feels far away, so its rewards and costs barely move you today. Picturing it in detail closes that distance.",
      "\"This will matter someday\" does very little. \"At 9pm tonight, if this is done, I'll be able to relax\" does much more.",
      "The more specific the picture, the closer the feeling gets, and the more it pulls you toward starting.",
    ],
    research: [
      "Hershfield, H.E. (2011). Future self-continuity: How conceptions of the future self transform intertemporal choice. Annals of the New York Academy of Sciences.",
    ],
  },
  s18: {
    paras: [
      "Putting a name to the exact emotion you're feeling makes it less intense. \"I'm dreading this because it's tedious\" has less grip on you than a vague sense of dread.",
      "You don't have to make the feeling go away before you start. The aim is to act while you feel it.",
      "The thought changes from \"I need to feel better before I can start\" to \"I can start while feeling uncomfortable.\"",
    ],
    research: [
      "Lieberman, M.D., et al. (2007). Putting feelings into words. Psychological Science.",
      "Hayes, S.C., et al. (2006). Acceptance and commitment therapy. Behaviour Research and Therapy.",
    ],
  },
  s19: {
    paras: [
      "\"I have to\" makes a task feel like something forced on you, and you push back against that.",
      "Saying \"I'm choosing to do this, because…\" changes how the task feels. Adding even a short reason of your own makes you more likely to follow through.",
      "This isn't pretending. In most cases you really are choosing, even when the choice feels limited.",
    ],
    research: [
      "Ryan, R.M. & Deci, E.L. (2000). Self-determination theory and the facilitation of intrinsic motivation, social development, and well-being. American Psychologist.",
    ],
  },
  s20: {
    paras: [
      "Perfectionism sets the bar for starting so high that you can't reach it.",
      "Give yourself clear permission to make an imperfect version, and make that the goal. Ask yourself what version of this would be good enough to move forward.",
      "That question has an answer. \"How do I make this perfect?\" doesn't, because the target keeps moving.",
    ],
    research: [],
  },
  s21: {
    paras: [
      "Make a deal with yourself: a set amount of time on the unpleasant task earns a set amount of time on something you enjoy.",
      "The hard thing comes first and the reward comes after. Decide both in advance, and keep the work short, like 15 to 30 minutes rather than the whole task.",
      "The unpleasant task becomes the price of admission rather than a barrier. Because you set the terms yourself, it feels like your choice rather than a chore.",
    ],
    research: [
      "Premack, D. (1959). Toward empirical behavior laws: I. Positive reinforcement. Psychological Review.",
    ],
  },
  s22: {
    paras: [
      "If you only work when you feel like it, your mood decides what gets done. On many days the honest answer to \"Do I feel like it?\" is no.",
      "Ask a different question: what does the person I want to be do today? Your sense of who you are is steadier than your mood. That makes it a more reliable thing to lean on.",
      "This helps most when fear of failure is in the way. Who you are doesn't depend on any one piece of work being excellent. It depends on showing up.",
    ],
    research: [
      "Pressfield, S. (2002). The War of Art. Black Irish Entertainment.",
      "Clear, J. (2018). Atomic Habits. Avery.",
      "Deci, E.L. & Ryan, R.M. (1985). Intrinsic motivation and self-determination in human behavior. Plenum.",
    ],
  },
  s23: {
    paras: [
      "Your mood changes what you think you can do. In a flat or low mood you see fewer options, feel less capable and find it harder to start.",
      "That isn't about the tasks. It's about the state you're in when you look at them.",
      "A positive or purposeful mood widens what seems possible. Getting into one before you start isn't a detour. It's part of getting started.",
    ],
    research: [
      "Fredrickson, B.L. (1998). What good are positive emotions? Review of General Psychology.",
      "Fredrickson, B.L. (2001). The role of positive emotions in positive psychology. American Psychologist.",
      "Fredrickson, B.L. & Joiner, T. (2002). Positive emotions trigger upward spirals toward emotional well-being. Psychological Science.",
    ],
  },
  s24: {
    paras: [
      "Pair a task you dread with something you enjoy, like a favourite podcast, a great coffee or a show you only let yourself watch during this task.",
      "The rule matters. If you only get the treat while you're doing the task, you have a real reason to start.",
      "The task stays the same, but the experience of doing it changes. Over time the task itself starts to feel more pleasant.",
    ],
    research: [
      "Milkman, K.L., Minson, J.A., & Volpp, K.G.M. (2014). Holding the Hunger Games hostage at the gym: An evaluation of temptation bundling. Management Science.",
    ],
  },
  s25: {
    paras: [
      "If you put things off a lot, every free hour can feel like time stolen from work. Leisure starts to feel guilty, and that background guilt makes it harder to work.",
      "Try planning the other way round. Book your leisure, social and personal time first, then fit work into the windows that are left.",
      "Rest you've planned carries no guilt. Work stops being an endless obligation and becomes a defined block of time. You'll also see how little free time you really have, and your plans get more honest as a result.",
    ],
    research: [
      "Fiore, N. (1989). The Now Habit. Tarcher/Putnam.",
    ],
  },
  s26: {
    paras: [
      "Do the task you're most likely to avoid first thing, before you let yourself near the easier ones.",
      "A hard task left waiting builds dread, and the dread makes every other task heavier. Once it's done, you carry nothing for the rest of the day.",
      "The worst moment is usually the one before you start. You also tend to have more energy early in the day, and finishing something hard early gives you momentum.",
    ],
    research: [
      "Tracy, B. (2001). Eat That Frog! Berrett-Koehler.",
    ],
  },
  s27: {
    paras: [
      "You tend to do whatever is easiest in the space around you. So change the space instead of relying on willpower.",
      "Make the right thing easier: lay out your running clothes the night before, or leave the document open before you close the laptop. Make the temptation harder: take social media off your home screen, or leave your phone in another room.",
      "Unlike a plan in your head, these changes keep working when you're tired, distracted or not thinking about your goals.",
    ],
    research: [
      "Clear, J. (2018). Atomic Habits. Avery.",
      "Wood, W. & Neal, D.T. (2007). A new look at habits and the habit-goal interface. Psychological Review.",
      "Thaler, R.H. & Sunstein, C.R. (2008). Nudge. Yale University Press.",
    ],
  },
};
